/**
 * "This module is not licensed" signals from the backend.
 *
 * A service that enforces org licensing answers 403 with a body carrying
 * `code: "module_not_licensed"` and the module's slug (saf3ai_auth.entitlement
 * denial_body). Some services put that body inside an envelope — security-ms
 * wraps every error as {success, error} and FastAPI may nest it under
 * `detail` — so all three places are read.
 *
 * Every API client calls reportModuleNotLicensed() on a 403. It dispatches
 * MODULE_NOT_LICENSED_EVENT, and ModuleLockBoundary (inside AppLayout) swaps
 * the page being shown for the lock screen. This catches pages that slip past
 * route gating — an Atlas page calling an endpoint pinned to one module, say.
 * The request still rejects as before, so callers' own 403 handling is
 * unchanged.
 *
 * Requests made by the page CHROME (header bell, sidebar badges, the catalog
 * fetch itself) must not lock the page: they pass `skipModuleLock: true` in
 * their axios config (or `background: true` here).
 *
 * Deliberately dependency-free: every API client imports it.
 */
export const MODULE_NOT_LICENSED_EVENT = 'saf3ai:module-not-licensed';
export const NOT_LICENSED_CODE = 'module_not_licensed';

/** The denial body (with `module`) if `body` is one, else null. */
export function findNotLicensedBody(body) {
  if (!body || typeof body !== 'object') return null;
  const candidates = [body, body.detail, body.error, body.error && body.error.details, body.error && body.error.detail];
  for (const c of candidates) {
    if (c && typeof c === 'object' && c.code === NOT_LICENSED_CODE) return c;
  }
  return null;
}

/**
 * Report a response. Returns true when it was a module_not_licensed 403 (and
 * the event was dispatched), false otherwise.
 */
export function reportModuleNotLicensed(status, body, { background = false, url = '' } = {}) {
  if (Number(status) !== 403) return false;
  const denial = findNotLicensedBody(body);
  if (!denial) return false;
  if (background || typeof window === 'undefined') return true;
  window.dispatchEvent(new CustomEvent(MODULE_NOT_LICENSED_EVENT, {
    detail: {
      module: denial.module || null,
      label: denial.module_label || null,
      requiredAnyOf: Array.isArray(denial.required_any_of) ? denial.required_any_of : [],
      url,
    },
  }));
  return true;
}

/** Axios response-error hook: call from each client's interceptor. Never throws. */
export function reportAxiosModuleNotLicensed(error) {
  try {
    const res = error && error.response;
    if (!res) return false;
    const config = error.config || {};
    const hit = reportModuleNotLicensed(res.status, res.data, {
      background: Boolean(config.skipModuleLock),
      url: config.url || '',
    });
    if (hit) error.moduleNotLicensed = findNotLicensedBody(res.data);
    return hit;
  } catch {
    return false;
  }
}
